/**
 * PhoneVerificationPanel — the SMS gate rendered inline on the business step,
 * in place of the business inputs. It owns the challenge lifecycle: sending the
 * code on arrival (unless the caller already has one cached), resending under a
 * cooldown, and handing the verification token back once the digits check out.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, MessageSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  otpErrorCode,
  resendRegistrationOtp,
  sendRegistrationOtp,
  verifyRegistrationOtp,
  type OtpErrorCode,
} from 'features/onboarding/api/otp';
import { logger } from 'shared/lib/logger';
import type { SupportedLanguage } from 'shared/i18n/i18n';
import { cn } from 'shared/lib/utils';

const CODE_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;

const ERROR_KEYS: Record<OtpErrorCode, string> = {
  OTP_INVALID: 'otp.errors.invalid',
  OTP_EXPIRED: 'otp.errors.expired',
  OTP_LOCKED: 'otp.errors.locked',
  OTP_RESEND_COOLDOWN: 'otp.errors.cooldown',
  OTP_NOT_FOUND: 'otp.errors.notFound',
  OTP_SEND_FAILED: 'otp.errors.sendFailed',
  UNKNOWN: 'otp.errors.unknown',
};

interface PhoneVerificationPanelProps {
  phone: string;
  language: SupportedLanguage;
  /** Cached challenge, so returning to this step does not burn a new SMS. */
  challengeId: string | null;
  sentTo: string;
  onChallenge: (challengeId: string, sentTo: string) => void;
  onVerified: (token: string) => void;
  onChangePhone: () => void;
}

const PhoneVerificationPanel: React.FC<PhoneVerificationPanelProps> = ({
  phone,
  language,
  challengeId,
  sentTo,
  onChallenge,
  onVerified,
  onChangePhone,
}) => {
  const { t } = useTranslation();

  const [activeChallengeId, setActiveChallengeId] = useState<string | null>(
    challengeId,
  );
  const [digits, setDigits] = useState<string[]>(() => Array(CODE_LENGTH).fill(''));
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [errorKey, setErrorKey] = useState<string | undefined>();
  const [cooldown, setCooldown] = useState(0);

  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const code = digits.join('');

  const onChallengeRef = useRef(onChallenge);
  onChallengeRef.current = onChallenge;

  const request = useCallback(
    async (existingChallengeId: string | null) => {
      setSending(true);
      setErrorKey(undefined);
      try {
        const result = existingChallengeId
          ? await resendRegistrationOtp(existingChallengeId)
          : await sendRegistrationOtp(phone, language);
        setActiveChallengeId(result.challengeId);
        // A resend may answer without echoing the target; keep the known one so
        // the masked number in the header does not disappear.
        onChallengeRef.current(result.challengeId, result.sentTo || sentTo);
        setDigits(Array(CODE_LENGTH).fill(''));
        setCooldown(RESEND_COOLDOWN_SECONDS);
        inputsRef.current[0]?.focus();
      } catch (err) {
        logger.error('[HAAT Partner Connect] otp send failed', {
          message: err instanceof Error ? err.message : String(err),
        });
        setErrorKey(ERROR_KEYS[otpErrorCode(err)]);
      } finally {
        setSending(false);
      }
    },
    [phone, language, sentTo],
  );

  // Send the first challenge on arrival, but only when the caller has none
  // cached. The ref survives a double-invoked effect (StrictMode), which would
  // otherwise cost the partner a second SMS.
  const sentOnceRef = useRef(false);
  useEffect(() => {
    if (challengeId || sentOnceRef.current) return;
    sentOnceRef.current = true;
    void request(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = window.setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  const handleVerify = useCallback(async () => {
    if (!activeChallengeId || code.length !== CODE_LENGTH || verifying) return;
    setVerifying(true);
    setErrorKey(undefined);
    try {
      const result = await verifyRegistrationOtp(activeChallengeId, code);
      onVerified(result.verificationToken);
    } catch (err) {
      setErrorKey(ERROR_KEYS[otpErrorCode(err)]);
      setDigits(Array(CODE_LENGTH).fill(''));
      inputsRef.current[0]?.focus();
    } finally {
      setVerifying(false);
    }
  }, [activeChallengeId, code, verifying, onVerified]);

  // Auto-submit the moment the last digit lands — nobody wants to reach for a
  // button after typing a code they just read off a notification.
  useEffect(() => {
    if (code.length === CODE_LENGTH && !verifying && activeChallengeId) {
      void handleVerify();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const setDigitAt = (index: number, value: string) => {
    setDigits((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleDigitChange = (index: number, raw: string) => {
    const cleaned = raw.replace(/\D/g, '');
    if (!cleaned) {
      setDigitAt(index, '');
      return;
    }
    // A paste lands entirely in one box; spread it across the remaining ones.
    if (cleaned.length > 1) {
      setDigits((prev) => {
        const next = [...prev];
        cleaned
          .slice(0, CODE_LENGTH - index)
          .split('')
          .forEach((ch, offset) => {
            next[index + offset] = ch;
          });
        return next;
      });
      const landed = Math.min(index + cleaned.length, CODE_LENGTH - 1);
      inputsRef.current[landed]?.focus();
      return;
    }
    setDigitAt(index, cleaned);
    if (index < CODE_LENGTH - 1) inputsRef.current[index + 1]?.focus();
  };

  const handleKeyDown = (
    index: number,
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      event.preventDefault();
      setDigitAt(index - 1, '');
      inputsRef.current[index - 1]?.focus();
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      void handleVerify();
    }
  };

  const busy = sending || verifying;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-8">
      <div className="flex flex-col items-center text-center">
        <span className="w-14 h-14 rounded-2xl bg-haat-red-50 text-haat-red-600 flex items-center justify-center mb-4">
          <MessageSquare size={26} />
        </span>

        <div dir="ltr" className="flex items-center justify-center gap-2">
          {digits.map((digit, index) => (
            <input
              // Index is the identity here: the boxes are positional.
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              ref={(el) => {
                inputsRef.current[index] = el;
              }}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              aria-label={t('otp.digitLabel', { index: index + 1 }) as string}
              maxLength={CODE_LENGTH}
              disabled={busy}
              value={digit}
              onChange={(e) => handleDigitChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              className={cn(
                'h-12 w-11 rounded-xl border text-center text-lg font-bold text-gray-900',
                'focus:outline-none focus:ring-2 focus:ring-haat-red-600/40 focus:border-haat-red-600',
                'disabled:bg-gray-50 disabled:text-gray-400',
                errorKey ? 'border-red-300' : 'border-gray-300',
              )}
            />
          ))}
        </div>

        {errorKey && (
          <p
            role="alert"
            className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-red-600"
          >
            <AlertTriangle size={14} />
            {t(errorKey)}
          </p>
        )}

        <button
          type="button"
          onClick={handleVerify}
          disabled={busy || code.length !== CODE_LENGTH}
          className="mt-6 btn-haat w-full max-w-sm inline-flex items-center justify-center gap-2"
        >
          {verifying ? (
            <>
              <span className="loading-spinner h-4 w-4 border-white/40 border-t-white" />
              {t('otp.verifying')}
            </>
          ) : (
            t('otp.verify')
          )}
        </button>

        <button
          type="button"
          onClick={() => void request(activeChallengeId)}
          disabled={busy || cooldown > 0}
          className="mt-3 text-sm font-medium text-haat-red-600 hover:text-haat-red-700 disabled:text-gray-400 disabled:cursor-not-allowed"
        >
          {sending
            ? t('otp.sending')
            : cooldown > 0
              ? t('otp.resendIn', { seconds: cooldown })
              : t('otp.resend')}
        </button>

        <button
          type="button"
          onClick={onChangePhone}
          className="mt-2 text-xs font-medium text-gray-500 hover:text-gray-700"
        >
          {t('otp.changePhone')}
        </button>
      </div>
    </div>
  );
};

export default PhoneVerificationPanel;
